import { FC, RefObject, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { colors } from '../../const/colors'
import FullScreenLoader from '../Shared/FullScreenLoader'
import DetachUserModal from './DetachUserModal'
import { IUserDataRow } from '../Parking/ParkingPoolUsersTable'
import { IParkingPoolProfile } from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import { useParkingUserDetails } from './useParkingUserDetails'
import UserDetailsHeader from './UserDetailsHeader'
import UserDetailsTabs, { UserDetailsTab } from './UserDetailsTabs'
import AssignToServiceAction from './AssignToServiceAction'
import UserIdentityCard from './UserIdentityCard'
import AccessCardSection from './AccessCardSection'
import CarPlatesSection from './CarPlatesSection'
import ServiceProfileSection from './ServiceProfileSection'
import ReservationsSection from './ReservationsSection'
import CancelledReservationsSection from './CancelledReservationsSection'

const UserDetails: FC<{
  userData: IUserDataRow
  closeDrawer: () => void
  listRefetch: () => void
  tenantName: string
  parkingPoolId: string
  profiles: IParkingPoolProfile[]
}> = ({
  userData,
  closeDrawer,
  listRefetch,
  tenantName,
  parkingPoolId,
  profiles,
}) => {
  const [tab, setTab] = useState<UserDetailsTab>('settings')
  const platesRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)
  const assignRef = useRef<HTMLDivElement>(null)
  const details = useParkingUserDetails({
    userData,
    tenantName,
    parkingPoolId,
    profiles,
    listRefetch,
  })

  useEffect(() => {
    if (!details.assigned) setTab('settings')
  }, [details.assigned])

  if (details.loading || !details.user) {
    return <FullScreenLoader />
  }

  const { user } = details
  const { fullName, firstName, lastName, workEmail } = userData
  const checklist = !details.assigned

  const goToStep = (ref: RefObject<HTMLDivElement>) => {
    if (!checklist) return
    requestAnimationFrame(() =>
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg,
      }}
    >
      <UserDetailsHeader onClose={closeDrawer} />

      <UserDetailsTabs
        value={tab}
        onChange={setTab}
        reservationsLocked={!details.assigned}
        reservationsCount={details.activeReservations.length}
      />

      <Box
        data-e2e="user-details-content"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: '20px 24px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        {tab === 'settings' ? (
          <>
            <UserIdentityCard
              fullName={fullName}
              login={user.login}
              firstName={firstName}
              lastName={lastName}
              workEmail={workEmail}
              assigned={details.assigned}
              onDetach={details.requestDetach}
            />

            <AccessCardSection
              login={user.login}
              accessCard={user.accessCard}
              assigned={details.assigned}
              refetch={details.refetch}
              step={checklist ? 1 : undefined}
              stepDone={details.hasCard}
              onSaved={() => goToStep(platesRef)}
            />

            <div ref={platesRef}>
              <CarPlatesSection
                plates={details.plates}
                canEditPlates={details.canEditPlates}
                platesDirty={details.platesDirty}
                onUpdatePlate={details.updatePlate}
                onRemovePlate={details.removePlate}
                onAddPlate={details.addPlate}
                onSave={async () => {
                  if (await details.handleSavePlates()) goToStep(profileRef)
                }}
                step={checklist ? 2 : undefined}
                stepDone={details.platesSaved && !details.platesDirty}
              />
            </div>

            <div ref={profileRef}>
              <ServiceProfileSection
                assigned={details.assigned}
                profileId={details.profileId}
                profileOptions={details.profileOptions}
                profileDirty={details.profileDirty}
                onSelectProfile={details.setProfileId}
                onSave={async () => {
                  if (await details.handleSaveProfile()) goToStep(assignRef)
                }}
                step={checklist ? 3 : undefined}
                stepDone={details.profileSatisfied}
              />
            </div>

            {checklist && (
              <div ref={assignRef}>
                <AssignToServiceAction
                  canAssign={details.canAssign}
                  assignHint={details.assignHint}
                  onAssign={details.handleAssign}
                  step={4}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <ReservationsSection
              activeReservations={details.activeReservations}
              userName={fullName}
              login={user.login}
              parkingPoolId={parkingPoolId}
              notify={details.notify}
              onChanged={details.refetchAll}
            />
            <CancelledReservationsSection
              cancellations={details.cancellations}
            />
          </>
        )}
      </Box>

      <DetachUserModal
        open={details.detachOpen}
        userName={fullName}
        hasCard={details.hasCard}
        hasActiveReservations={!!details.activeReservations.length}
        withCard={details.detachWithCard}
        setWithCard={details.setDetachWithCard}
        onClose={() => details.setDetachOpen(false)}
        onConfirm={details.handleDetach}
      />
    </Box>
  )
}

export default UserDetails
